/**
 * Sentry integration — client-side error tracking + session replay.
 *
 * Why: before beta, we have zero external visibility into user errors. If
 * planner A hits a render crash, the page goes white and we never hear about
 * it. Sentry groups errors by stack-trace fingerprint (so 100 occurrences of
 * the same bug = 1 triageable issue), attaches user context (which planner),
 * and captures a masked session replay for the ~5s leading up to the error.
 *
 * Wiring:
 *   - main.jsx calls initSentry() before createRoot
 *   - AuthContext calls setSentryUser/clearSentryUser on auth changes
 *   - main.jsx wraps <App /> in <AppErrorBoundary>
 *     (see components/AppErrorBoundary.jsx)
 *
 * Env vars:
 *   VITE_SENTRY_DSN      Required for production tracking. Without it, init
 *                        is a no-op — local dev / CI / first builds before
 *                        signup work fine. Sign up at sentry.io, create a
 *                        React project, paste the DSN into .env.local and
 *                        into the Netlify build env vars.
 *   VITE_SENTRY_RELEASE  Sprint LL: release identifier that matches the
 *                        sourcemap upload. vite.config.js forwards Netlify's
 *                        COMMIT_REF (falling back to 'dev') into this, so
 *                        every event is linked to the exact deploy + source
 *                        line. Without a match, Sentry falls back to
 *                        minified stack traces.
 *
 * Privacy: session replay uses Sentry's safe defaults — maskAllText +
 * maskAllInputs + blockAllMedia. We see DOM structure and click paths but
 * not actual text/input content. Good enough to debug most beta issues
 * without exfiltrating client data. Loosen later if we need fidelity and
 * the beta cohort consents.
 */
import * as Sentry from '@sentry/react'

let initialized = false

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    if (import.meta.env.PROD) {
      // Prod build shipped without a DSN — surface loudly so the ops person
      // knows tracking is silently off.
      console.warn('[sentry] VITE_SENTRY_DSN not set — error tracking disabled')
    }
    return
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.PROD ? 'production' : 'development',
    // Sprint LL: tag every event with the exact build so Sentry can resolve
    // the uploaded sourcemap for that deploy. Falls back to undefined in
    // dev, which Sentry quietly ignores.
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    // user IP + user-agent; email is attached via setUser once auth resolves.
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Defaults are already safe — being explicit for future reviewers.
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    // Performance — 10% transaction sample. Plenty of signal at beta scale,
    // well under the free tier event budget.
    tracesSampleRate: 0.1,
    // Session replay — 10% of sessions baseline, but 100% when an error
    // happens (that's where the debugging value is highest: we get the
    // user's last ~5s before the crash).
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })

  initialized = true
}

export function setSentryUser(user) {
  if (!initialized) return
  if (!user) {
    Sentry.setUser(null)
    return
  }
  Sentry.setUser({ id: user.id, email: user.email })
}

export function clearSentryUser() {
  if (!initialized) return
  Sentry.setUser(null)
}

// Re-export so callers (ErrorBoundary) can pull everything from one place.
export { Sentry }
